// Procedural mech art, built once at boot (BootScene) with Canvas2D:
//   - body + glow sheets (76 frames each, 10 columns, numeric frame names) from a
//     parametric cutout rig, plus the Phaser animations over the body sheet
//   - per-frame MechFrameMeta derived from the same solved skeleton
//   - separate arm parts, six weapon arts, fourteen equipment modules
//   - a 512×512 hangar portrait and a full composite preview sheet per mech
//   - art.sheetsMeta[id]: the JSON-serialisable sheet + metadata export
// Any texture key that already exists (loaded art) is kept; the metadata is
// registered regardless so MechView always has attachment data.

import type Phaser from 'phaser';
import { CELL, FOOT_ANCHOR } from '../../config/constants';
import { MECH_IDS, ANIM_FRAMES, ANIM_KEYS, EQUIP_IDS, WEAPON_IDS, type AnimKey, type EquipVisual, type MechAnimInfo, type MechArt, type MechId, type VisualPiece, type WeaponArt, type WeaponId } from '../../core/types';
import { MECHS } from '../../data/mechs';
import { mechKeys } from '../keys';
import { art } from '../registry';
import { ARM_LIBS } from './arms';
import { CompositeCache, compositeFrame, type CompositeParts } from './compose';
import { applyCutoutStyle, applyWeaponCutouts, cutoutFor, hasEquipPart, loadCutouts, loadEquipParts, withEquipment } from './cutout';
import { canvas, ctx2d } from './draw';
import { EQUIP_SPECS, SLOT_FIT, SLOT_MOUNTS, equipPalette } from './equipment';
import { ANIM_START, buildSheets, cellXY, exportMeta, SHEET_COLUMNS, SHEET_ROWS, SHEET_SCALE, solveFrames, type SolvedFrame } from './export';
import { CellPainter } from './painter';
import { ANIM_TIMING } from './poses';
import { addCanvasTexture, renderSprite, type Sprite } from './sprite';
import { MECH_STYLES, type MechStyle } from './styles';
import { WEAPON_SPECS, weaponPalette } from './weapons';

export { poseArms } from './compose';

/** Portrait render scale and size. */
const PORTRAIT = 512;
const PORTRAIT_SCALE = 4;
/** Aim used for posed composites (slightly below horizontal: a relaxed ready stance). */
const READY_AIM = 0.08;

// ───────────────────────────── Weapons ─────────────────────────────

const weaponSprites = new Map<string, Sprite>();

function weaponSprite(id: WeaponId, scale: number): Sprite {
  const k = `${id}@${scale}`;
  let sp = weaponSprites.get(k);
  if (!sp) {
    const spec = WEAPON_SPECS[id];
    sp = renderSprite(spec.bounds, weaponPalette(spec), (d) => spec.draw(d), scale);
    weaponSprites.set(k, sp);
  }
  return sp;
}

function weaponArt(id: WeaponId, S = 1): WeaponArt {
  const spec = WEAPON_SPECS[id];
  const sp = weaponSprite(id, S);
  return {
    key: mechKeys.weapon(id),
    texScale: S,
    origin: { x: sp.ox / sp.body.width, y: sp.oy / sp.body.height },
    muzzle: { ...spec.muzzle },
    foregrip: { ...spec.foregrip },
    holdReach: spec.holdReach,
    holdDrop: spec.holdDrop,
    length: Math.round(spec.muzzle.x - spec.bounds.x0),
    glowKey: sp.glow ? mechKeys.weaponGlow(id) : undefined,
    glowColor: spec.glowColor,
  };
}

function buildWeapons(scene: Phaser.Scene): void {
  for (const id of WEAPON_IDS) {
    const sp = weaponSprite(id, SHEET_SCALE);
    addCanvasTexture(scene, mechKeys.weapon(id), sp.body);
    if (sp.glow) addCanvasTexture(scene, mechKeys.weaponGlow(id), sp.glow);
    art.weapons[id] = weaponArt(id, SHEET_SCALE);
  }
}

// ───────────────────────────── Equipment ─────────────────────────────

function buildEquipment(scene: Phaser.Scene): void {
  for (const id of EQUIP_IDS) {
    const spec = EQUIP_SPECS[id];
    const pal = equipPalette(spec);
    const pieces: { key: string; glowKey?: string; ox: number; oy: number }[] = [];
    for (const p of spec.pieces) {
      const sp = renderSprite(p.bounds, pal, (d) => p.draw(d), SHEET_SCALE);
      const key = mechKeys.equip(id, p.part);
      addCanvasTexture(scene, key, sp.body);
      let glowKey: string | undefined;
      if (sp.glow) {
        glowKey = mechKeys.equip(id, `${p.part}_glow`);
        addCanvasTexture(scene, glowKey, sp.glow);
      }
      pieces.push({ key, glowKey, ox: sp.ox / sp.body.width, oy: sp.oy / sp.body.height });
    }
    const visual = (mech: MechId): EquipVisual => {
      const fit = SLOT_FIT[mech][spec.slot];
      const out: VisualPiece[] = [];
      for (const p of pieces) {
        for (const [attach, layer] of SLOT_MOUNTS[spec.slot]) {
          const piece: VisualPiece = {
            key: p.key,
            attach,
            layer,
            dx: fit.dx,
            dy: fit.dy,
            rot: 0,
            originX: p.ox,
            originY: p.oy,
            followRot: true,
          };
          piece.scale = fit.scale / SHEET_SCALE;
          if (p.glowKey) piece.glowKey = p.glowKey;
          out.push(piece);
        }
      }
      const v: EquipVisual = { pieces: out };
      if (spec.dashTrail) v.dashTrail = spec.dashTrail;
      if (spec.reactorGlow !== undefined) v.reactorGlow = spec.reactorGlow;
      return v;
    };
    // Generic visual (VOLT fit) plus per-mech fits keyed `${equipId}@${mechId}`.
    art.visuals[id] = visual('volt');
    for (const mech of MECH_IDS) art.visuals[`${id}@${mech}`] = visual(mech);
  }
}

// ───────────────────────────── Mechs ─────────────────────────────

interface ArmSprites {
  upper: Sprite;
  fore: Sprite;
}

function armSprites(style: MechStyle, scale: number): ArmSprites {
  const lib = ARM_LIBS[style.id];
  const pal = style.pal;
  return {
    upper: renderSprite(lib.upperBounds, pal, (d) => lib.upper(d, style.arm.upperLen), scale),
    fore: renderSprite(lib.foreBounds, pal, (d) => lib.fore(d, style.arm.foreLen), scale),
  };
}

function compositeParts(style: MechStyle, arms: ArmSprites, weapon: WeaponId, scale: number): CompositeParts {
  return {
    upper: arms.upper,
    fore: arms.fore,
    upperLen: style.arm.upperLen,
    foreLen: style.arm.foreLen,
    rearTint: style.arm.rearTint,
    reactor: MECHS[style.id].reactorColor,
    weapon: { sprite: weaponSprite(weapon, scale), art: weaponArt(weapon) },
  };
}

function addSheet(scene: Phaser.Scene, key: string, c: HTMLCanvasElement): void {
  if (scene.textures.exists(key)) return;
  const tex = scene.textures.addCanvas(key, c);
  if (!tex) return;
  // Frame size follows the canvas: 1x preview sheets and 2x gameplay sheets share this.
  const S = c.width / (SHEET_COLUMNS * CELL);
  for (let i = 0; i < SHEET_COLUMNS * SHEET_ROWS; i++) {
    const { x, y } = cellXY(i);
    tex.add(i, 0, x * S, y * S, CELL * S, CELL * S);
  }
}

/** Composite preview sheet (arms + starting weapon posed per frame). */
function buildPreview(style: MechStyle, frames: SolvedFrame[], body: HTMLCanvasElement, glow: HTMLCanvasElement, parts: CompositeParts): HTMLCanvasElement {
  const out = canvas(body.width, body.height);
  const g = ctx2d(out);
  const cache = new CompositeCache();
  const cellBody = canvas(CELL, CELL);
  const cellGlow = canvas(CELL, CELL);
  const cb = ctx2d(cellBody);
  const cg = ctx2d(cellGlow);
  frames.forEach((f, i) => {
    const { x, y } = cellXY(i);
    cb.clearRect(0, 0, CELL, CELL);
    cg.clearRect(0, 0, CELL, CELL);
    const S = body.width / (SHEET_COLUMNS * CELL);
    cb.drawImage(body, x * S, y * S, CELL * S, CELL * S, 0, 0, CELL, CELL);
    cg.drawImage(glow, x * S, y * S, CELL * S, CELL * S, 0, 0, CELL, CELL);
    g.save();
    g.beginPath();
    g.rect(x, y, CELL, CELL);
    g.clip();
    compositeFrame(g, x, y, 1, cellBody, cellGlow, f.meta, parts, cache, READY_AIM);
    g.restore();
  });
  void style;
  return out;
}

/** 512×512 hangar render of the neutral idle pose at 4×, arms posed, starting weapon in hand. */
function buildPortrait(style: MechStyle, frame: SolvedFrame): HTMLCanvasElement {
  const S = PORTRAIT_SCALE;
  const painter = new CellPainter(CELL, S);
  const size = painter.size;
  const body = canvas(size, size);
  const glow = canvas(size, size);
  painter.render(style, frame.sk, frame.spec.fx, ctx2d(body), ctx2d(glow), 0, 0);
  const parts = compositeParts(style, armSprites(style, S), MECHS[style.id].startingWeapon, S);
  const full = canvas(size, size);
  compositeFrame(ctx2d(full), 0, 0, S, body, glow, frame.meta, parts, new CompositeCache(), READY_AIM);
  // Centre the alpha bounds inside the portrait (keeps the ground contact low).
  const data = ctx2d(full).getImageData(0, 0, size, size).data;
  let x0 = size;
  let x1 = 0;
  let y0 = size;
  let y1 = 0;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (data[(y * size + x) * 4 + 3] > 12) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  const out = canvas(PORTRAIT, PORTRAIT);
  const g = ctx2d(out);
  if (x1 <= x0 || y1 <= y0) return out;
  const w = x1 - x0 + 1;
  const h = y1 - y0 + 1;
  const fit = Math.min(1, (PORTRAIT - 40) / w, (PORTRAIT - 40) / h);
  const dw = w * fit;
  const dh = h * fit;
  g.imageSmoothingQuality = 'high';
  g.drawImage(full, x0, y0, w, h, (PORTRAIT - dw) / 2, PORTRAIT - 20 - dh - (PORTRAIT - 40 - dh) * 0.35, dw, dh);
  return out;
}

function buildAnims(scene: Phaser.Scene, id: MechId): void {
  const sheet = mechKeys.sheet(id);
  if (!scene.textures.exists(sheet)) return;
  for (const anim of ANIM_KEYS) {
    const key = mechKeys.anim(id, anim);
    if (scene.anims.exists(key)) continue;
    const start = ANIM_START[anim];
    const frames = Array.from({ length: ANIM_FRAMES[anim] }, (_, i) => ({ key: sheet, frame: start + i }));
    scene.anims.create({ key, frames, frameRate: ANIM_TIMING[anim].fps, repeat: ANIM_TIMING[anim].loop ? -1 : 0 });
  }
}

function buildMech(scene: Phaser.Scene, id: MechId): void {
  const style = MECH_STYLES[id];
  const keys = {
    sheet: mechKeys.sheet(id),
    glow: mechKeys.glow(id),
    preview: mechKeys.preview(id),
    portrait: mechKeys.portrait(id),
    armUpper: mechKeys.armUpper(id),
    armFore: mechKeys.armFore(id),
    armUpperGlow: `${mechKeys.armUpper(id)}_glow`,
    armForeGlow: `${mechKeys.armFore(id)}_glow`,
  };
  const ex = (k: string): boolean => scene.textures.exists(k);
  const needSheets = !ex(keys.sheet) || !ex(keys.glow) || !ex(keys.preview) || !ex(keys.portrait);

  let frames: SolvedFrame[];
  const arms = armSprites(style, 1);
  const armsHi = armSprites(style, SHEET_SCALE);
  if (needSheets) {
    const T0 = performance.now();
    const built = buildSheets(style);
    console.log('[t] sheets', id, (performance.now() - T0).toFixed(0));
    frames = built.frames;
    for (const w of built.warnings) console.warn(`[art-mech] ${w}`);
    addSheet(scene, keys.sheet, built.body);
    addSheet(scene, keys.glow, built.glow);
    if (!ex(keys.preview)) {
      const parts = compositeParts(style, arms, MECHS[id].startingWeapon, 1);
      const T1 = performance.now();
      addSheet(scene, keys.preview, buildPreview(style, frames, built.body, built.glow, parts));
      console.log('[t] preview', id, (performance.now() - T1).toFixed(0));
    }
    const T2 = performance.now();
    if (!ex(keys.portrait)) addCanvasTexture(scene, keys.portrait, buildPortrait(style, frames[ANIM_START.idle]));
    console.log('[t] portrait', id, (performance.now() - T2).toFixed(0));
  } else {
    frames = solveFrames(style);
  }

  addCanvasTexture(scene, keys.armUpper, armsHi.upper.body);
  addCanvasTexture(scene, keys.armFore, armsHi.fore.body);
  if (armsHi.upper.glow) addCanvasTexture(scene, keys.armUpperGlow, armsHi.upper.glow);
  if (armsHi.fore.glow) addCanvasTexture(scene, keys.armForeGlow, armsHi.fore.glow);
  buildAnims(scene, id);

  const animInfo = {} as Record<AnimKey, MechAnimInfo>;
  for (const anim of ANIM_KEYS) {
    const start = ANIM_START[anim];
    animInfo[anim] = {
      phaserKey: mechKeys.anim(id, anim),
      start,
      count: ANIM_FRAMES[anim],
      fps: ANIM_TIMING[anim].fps,
      loop: ANIM_TIMING[anim].loop,
      meta: frames.slice(start, start + ANIM_FRAMES[anim]).map((f) => f.meta),
    };
  }
  const arm: MechArt['arm'] = {
    upperKey: keys.armUpper,
    foreKey: keys.armFore,
    upperGlowKey: armsHi.upper.glow ? keys.armUpperGlow : undefined,
    foreGlowKey: armsHi.fore.glow ? keys.armForeGlow : undefined,
    upperLen: style.arm.upperLen,
    foreLen: style.arm.foreLen,
    upperOrigin: { x: armsHi.upper.ox / armsHi.upper.body.width, y: armsHi.upper.oy / armsHi.upper.body.height },
    foreOrigin: { x: armsHi.fore.ox / armsHi.fore.body.width, y: armsHi.fore.oy / armsHi.fore.body.height },
    rearTint: style.arm.rearTint,
  };
  art.mechs[id] = {
    mechId: id,
    sheetKey: keys.sheet,
    glowSheetKey: keys.glow,
    anims: animInfo,
    arm,
    previewSheetKey: keys.preview,
    portraitKey: keys.portrait,
    cell: CELL,
    sheetScale: SHEET_SCALE,
    footAnchor: { x: FOOT_ANCHOR.x, y: FOOT_ANCHOR.y },
  };
  art.sheetsMeta[id] = exportMeta(
    style,
    frames,
    { body: keys.sheet, glow: keys.glow, preview: keys.preview, portrait: keys.portrait, armUpper: keys.armUpper, armFore: keys.armFore },
    { upperLen: arm.upperLen, foreLen: arm.foreLen, upperOrigin: arm.upperOrigin, foreOrigin: arm.foreOrigin, rearTint: arm.rearTint },
    (anim) => mechKeys.anim(id, anim),
  );
}

/** Builds every mech body/glow sheet, arm parts, weapon art and equipment modules; fills the art registry. */
export function buildMechArt(scene: Phaser.Scene): void {
  // Painted cutout parts (when loaded) re-proportion their mech before anything is solved.
  try {
    loadCutouts(scene);
    loadEquipParts(scene);
    applyWeaponCutouts(scene, WEAPON_SPECS);
    for (const id of MECH_IDS) {
      const c = cutoutFor(id);
      if (c) applyCutoutStyle(MECH_STYLES[id], c);
    }
  } catch (err) {
    console.warn('[art-mech] cutout setup failed; using vector parts', err);
  }
  // Weapons first: the preview sheets and portraits pose the starting weapon.
  buildWeapons(scene);
  for (const id of MECH_IDS) {
    try {
      buildMech(scene, id);
    } catch (err) {
      console.error(`[art-mech] building ${id} failed`, err);
    }
  }
  buildEquipment(scene);
}

// ───────────────────────────── Wearable loadouts ─────────────────────────────

/** Body sheet + arm textures of a mech wearing painted equipment (see cutout.ts). */
export interface LoadoutArt {
  sheetKey: string;
  glowKey: string;
  upperKey: string;
  upperGlowKey: string | null;
  /** Normalised origin of the upper-arm texture (the shoulder pivot). */
  upperOrigin: { x: number; y: number };
}

/** Frames baked per tick, so a gear change never stalls a frame for long. */
const BAKE_CHUNK = 6;
const loadouts = new Map<string, Promise<LoadoutArt | null>>();
/** Baked loadouts kept (two 1280x1024 canvases each); the oldest beyond this is freed. */
const MAX_LOADOUTS = 4;

/** Free the oldest cached loadouts (a view showing one re-requests and re-bakes it). */
function evictLoadouts(textures: Phaser.Textures.TextureManager): void {
  while (loadouts.size > MAX_LOADOUTS) {
    const [sig, job] = loadouts.entries().next().value as [string, Promise<LoadoutArt | null>];
    loadouts.delete(sig);
    void job.then((a) => {
      if (!a) return;
      for (const k of [a.sheetKey, a.glowKey, a.upperKey, a.upperGlowKey]) if (k && textures.exists(k)) textures.remove(k);
    });
  }
}

/** Painted equipment ids of this list, in a stable order (the cache key). */
export function paintedEquipment(mech: MechId, ids: readonly string[]): string[] {
  return ids.filter((e) => hasEquipPart(mech, e)).sort();
}

/**
 * The mech's sheets re-baked with its painted equipment parts swapped in (cached per loadout).
 * Resolves null when nothing in `ids` has a painted part (use the base art). Bakes a few
 * frames per tick in the background; the caller keeps showing the old art until it resolves.
 */
export function requestLoadoutArt(scene: Phaser.Scene, mech: MechId, ids: readonly string[]): Promise<LoadoutArt | null> {
  const equip = paintedEquipment(mech, ids);
  if (equip.length === 0) return Promise.resolve(null);
  const tag = equip.join('+');
  const sig = `${mech}@${tag}`;
  const cached = loadouts.get(sig);
  if (cached) {
    // Most recently used goes to the back of the eviction queue.
    loadouts.delete(sig);
    loadouts.set(sig, cached);
    return cached;
  }
  const textures = scene.sys.game.textures;
  const keys = {
    sheet: `${mechKeys.sheet(mech)}@${tag}`,
    glow: `${mechKeys.glow(mech)}@${tag}`,
    upper: `${mechKeys.armUpper(mech)}@${tag}`,
    upperGlow: `${mechKeys.armUpper(mech)}_glow@${tag}`,
  };
  const job = new Promise<LoadoutArt | null>((resolve) => {
    try {
      const style = MECH_STYLES[mech];
      const frames = solveFrames(style);
      const S = SHEET_SCALE;
      const body = canvas(SHEET_COLUMNS * CELL * S, SHEET_ROWS * CELL * S);
      const glow = canvas(SHEET_COLUMNS * CELL * S, SHEET_ROWS * CELL * S);
      const bg = ctx2d(body);
      const gg = ctx2d(glow);
      const painter = new CellPainter(CELL, S);
      let i = 0;
      const step = (): void => {
        try {
          withEquipment(mech, equip, () => {
            for (const end = Math.min(frames.length, i + BAKE_CHUNK); i < end; i++) {
              const { x, y } = cellXY(i);
              painter.render(style, frames[i].sk, frames[i].spec.fx, bg, gg, x * S, y * S);
            }
          });
          if (i < frames.length) {
            setTimeout(step, 0);
            return;
          }
          const arms = withEquipment(mech, equip, () => armSprites(style, S));
          const target = { textures } as unknown as Phaser.Scene;
          addSheet(target, keys.sheet, body);
          addSheet(target, keys.glow, glow);
          addCanvasTexture(target, keys.upper, arms.upper.body);
          if (arms.upper.glow) addCanvasTexture(target, keys.upperGlow, arms.upper.glow);
          resolve({
            sheetKey: keys.sheet,
            glowKey: keys.glow,
            upperKey: keys.upper,
            upperGlowKey: arms.upper.glow ? keys.upperGlow : null,
            upperOrigin: { x: arms.upper.ox / arms.upper.body.width, y: arms.upper.oy / arms.upper.body.height },
          });
        } catch (err) {
          console.warn('[art-mech] loadout bake failed', sig, err);
          resolve(null);
        }
      };
      step();
    } catch (err) {
      console.warn('[art-mech] loadout bake failed', sig, err);
      resolve(null);
    }
  });
  loadouts.set(sig, job);
  evictLoadouts(textures);
  return job;
}
